import React from 'react';

import { isBech32Address, fromBech32Address } from 'lib/address/bech32';
import useApiQuery from 'lib/api/useApiQuery';
import useDebounce from 'lib/hooks/useDebounce';
import { TRANSACTION_HASH_REGEXP } from 'toolkit/components/forms/validators/transaction';

export default function useQuickSearchQuery() {
  const [ searchTerm, setSearchTerm ] = React.useState('');

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const redirectCheckQuery = useApiQuery('general:search_check_redirect', {
    // Check redirect first to determine if resource exists
    queryParams: { q: debouncedSearchTerm },
    queryOptions: { enabled: Boolean(debouncedSearchTerm) },
  });

  // Only call quick_search if:
  // 1. It's not a transaction hash, OR
  // 2. It's a transaction hash but redirect check shows it doesn't exist (redirect: false)
  const shouldCallQuickSearch = React.useMemo(() => {
    if (!debouncedSearchTerm.trim()) return false;

    const isTransactionHash = TRANSACTION_HASH_REGEXP.test(debouncedSearchTerm);

    if (!isTransactionHash) {
      // Not a transaction hash, proceed with normal search
      return true;
    }

    // For transaction hashes, only search if redirect check is complete and shows no redirect
    if (redirectCheckQuery.data !== undefined) {
      return !redirectCheckQuery.data.redirect;
    }

    // If redirect check is still loading, don't call quick_search yet
    return false;
  }, [ debouncedSearchTerm, redirectCheckQuery.data ]);

  const query = useApiQuery('general:quick_search', {
    queryParams: { q: isBech32Address(debouncedSearchTerm) ? fromBech32Address(debouncedSearchTerm) : debouncedSearchTerm },
    queryOptions: { enabled: shouldCallQuickSearch },
  });

  return React.useMemo(() => ({
    searchTerm,
    debouncedSearchTerm,
    handleSearchTermChange: setSearchTerm,
    query,
    redirectCheckQuery,
  }), [ debouncedSearchTerm, query, redirectCheckQuery, searchTerm ]);
}
